import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The banks behind the initiative, as a row of logos.
 *
 * Shared by the web footer and the foot of the app's home screen, so the two
 * cannot drift on which banks appear or in what order. Everything comes from
 * the CMS (`partners`), so adding, reordering or hiding a bank is an edit, not
 * a release — which matters once the app is in the stores.
 *
 * Each tile is painted the logo's own background colour (`bg_color`, read
 * from the artwork's corners on upload), because the logos arrive as opaque
 * artwork — mostly on white, some not. The tile then continues the artwork
 * instead of framing it, and one tile size makes seven different files read
 * as a set. White when no colour is known.
 *
 * Takes the frontend's own `api` client, like useLocaleSync, because the app
 * and the microsite each build their own instance.
 */
export default function PartnerFooter({ api, className = '', showLabel = true }) {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.event
      .partners()
      .then((res) => {
        if (!cancelled) setPartners(res?.data || []);
      })
      // Decoration, not function: a failed fetch leaves the footer out rather
      // than putting an error at the bottom of the home screen.
      .catch((err) => console.error('[partners] load failed', err));
    return () => {
      cancelled = true;
    };
  }, [api]);

  const shown = partners.filter((p) => p.logo_url);
  if (!shown.length) return null;

  return (
    <section className={className} aria-label={t('partners.label')}>
      {/*
        Off where the surrounding section already names the banks (About).
        Sentence case rather than the tracked all-caps label used elsewhere:
        this is a full sentence naming the organiser, and at 10px with wide
        letter-spacing it wrapped into an unreadable block.
      */}
      {showLabel && (
        <p className="text-white/40 text-xs font-body text-center leading-relaxed max-w-[34rem] mx-auto mb-3">
          {t('partners.label')}
        </p>
      )}

      {/*
        Flex-wrap with fixed-width tiles rather than a grid, so a row that is
        not full — seven into threes leaves one — is centred instead of
        hanging off the left edge.
      */}
      <ul className="flex flex-wrap justify-center gap-2">
        {shown.map((p) => {
          const tile = (
            <span
              className="flex items-center justify-center rounded-lg h-14 px-3"
              style={{ backgroundColor: p.bg_color || '#ffffff' }}
            >
              <img
                src={p.logo_url}
                alt={p.name}
                loading="lazy"
                className="max-h-9 max-w-full w-auto object-contain"
              />
            </span>
          );
          return (
            <li key={p.id} className="basis-[calc(33.333%-0.5rem)] sm:basis-[calc(25%-0.5rem)]">
              {p.url ? (
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="block">
                  {tile}
                </a>
              ) : (
                tile
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
